import { useState } from 'react';
import { ArConnectWallet } from '../types';
import { isValidEvmAddress } from '../utils/ao';

interface EvmAddressLinkerProps {
  arConnectState: {
    isConnected: boolean;
    arweaveAddress: string | null;
    evmAddress: string | null;
    isLoading: boolean;
    error: string | null;
    isArConnectAvailable: boolean | ArConnectWallet;
    connectWallet: () => Promise<void>;
    disconnectWallet: () => Promise<void>;
    linkEvmAddress: (evmAddress: string) => Promise<boolean>;
    checkExistingLink: () => Promise<void>;
    clearError: () => void;
  };
  onBack?: () => void;
  showBackButton?: boolean;
}

export const EvmAddressLinker: React.FC<EvmAddressLinkerProps> = ({ 
  arConnectState, 
  onBack, 
  showBackButton 
}) => {
  const [inputAddress, setInputAddress] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [linkingSuccess, setLinkingSuccess] = useState(false);
  
  const { 
    isConnected, 
    evmAddress, 
    linkEvmAddress, 
    error,
    clearError,
    isLoading 
  } = arConnectState;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputAddress.trim()) {
      return;
    }

    clearError();
    setIsLinking(true);
    setLinkingSuccess(false);

    const success = await linkEvmAddress(inputAddress.trim());
    
    if (success) {
      setLinkingSuccess(true);
      setInputAddress('');
      // Clear success message after 3 seconds
      setTimeout(() => setLinkingSuccess(false), 3000);
    }
    
    setIsLinking(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputAddress(value);
    
    // Clear any previous errors when user starts typing
    if (error) {
      clearError();
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const isValidInput = inputAddress.trim() && isValidEvmAddress(inputAddress.trim());

  if (!isConnected) {
    return (
      <div className="glass rounded-xl p-6 holo-border">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-gray-400/20 to-gray-600/20 rounded-full flex items-center justify-center border border-gray-500/30">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Wallet Required</h3>
          <p className="text-gray-400 leading-relaxed">
            Please connect your ArConnect wallet first to link an EVM address.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-xl p-6 holo-border">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white mb-2">
          EVM Address Linking
        </h2>
        <p className="text-gray-400 leading-relaxed">
          Link your Ethereum/EVM compatible address to your Arweave wallet
        </p>
      </div>

      {/* Success Message */}
      {linkingSuccess && (
        <div className="mb-6 glass rounded-lg p-4 border border-green-500/30 glow-primary">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-green-300 font-medium">
                EVM address successfully linked to your Arweave wallet!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 glass rounded-lg p-4 border border-red-500/30 glow-accent">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-red-300">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={clearError}
                className="text-red-400"
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Linked Address */}
      {evmAddress && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Currently Linked Address
          </label>
          <div className="glass rounded-lg p-4 border border-gray-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <code className="text-white font-mono text-sm bg-black/20 px-3 py-1 rounded">
                  {formatAddress(evmAddress)}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(evmAddress)}
                  className="text-gray-400 hover:text-white p-1 rounded transition-colors"
                  title="Copy full address"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="evm-address" className="block text-sm font-medium text-gray-300 mb-3">
            {evmAddress ? 'Update EVM Address' : 'EVM Address'}
          </label>
          <div className="relative">
            <input
              type="text"
              id="evm-address"
              value={inputAddress}
              onChange={handleInputChange}
              placeholder="0x0000000000000000000000000000000000000000"
              className={`block w-full px-4 py-3 glass rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0  font-mono text-sm ${
                inputAddress && !isValidInput
                  ? 'border border-red-500/50 focus:ring-red-500/50 glow-accent'
                  : 'border border-cyan-500/30 focus:ring-cyan-500/50 glow-primary'
              }`}
              disabled={isLinking || isLoading}
            />
            {inputAddress && (
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                {isValidInput ? (
                  <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center border border-green-500/30">
                    <svg className="h-4 w-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center border border-red-500/30">
                    <svg className="h-4 w-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
              </div>
            )}
          </div>
          {inputAddress && !isValidInput && (
            <p className="mt-2 text-sm text-red-400">
              Please enter a valid EVM address (42 characters, starting with 0x)
            </p>
          )}
        </div>

        <div className="flex space-x-4">
          {showBackButton && (
            <button
              type="button"
              onClick={onBack}
              className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 px-6 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={!isValidInput || isLinking || isLoading}
            className={`${showBackButton ? 'flex-1' : 'w-full'} bg-cyan-500 text-black py-3 px-6 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed glow-secondary flex items-center justify-center`}
          >
            {isLinking ? (
              evmAddress ? 'Updating...' : 'Linking...'
            ) : (
              <>
                <span>Confirm</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
